import { Injectable } from "@angular/core";
import { DialogService } from "src/app/core/core-services/services/dialog.service";
import { WarningDialogComponent } from "src/app/core/core-services/warning-dialog/warning-dialog.component";
import { PlanFeatures } from "src/app/core/models/stripe.models";
import { take } from "rxjs/operators";

@Injectable({
    providedIn: "root",
})
export class TinyMceConfigService {
    // Direct commands that match e.command exactly
    private readonly directCommandMap: Partial<Record<keyof PlanFeatures, string[]>> = {
        Bold: ["Bold"],
        "Bullet Points": ["InsertUnorderedList"],
        "Numbered List": ["InsertOrderedList"],
        "Alignment - Left": ["JustifyLeft"],
        "Alignment - Centered": ["JustifyCenter"],
        "Alignment - Right": ["JustifyRight"],
        "Increase Indent": ["indent", "Indent"],
        "Decrease Indent": ["outdent", "Outdent"],
        "Clear Formatting": ["RemoveFormat"],
        Hyperlink: ["mceLink"],
        Preview: ["mcePreview"],
        "Source Code": ["mceCodeEditor"],
        Images: ["mceImage", "mceUpdateImage"],
    };

    // Commands that use mceToggleFormat with specific values
    private readonly toggleFormatMap: Partial<Record<keyof PlanFeatures, string[]>> = {
        Bold: ["bold"],
        Italic: ["italic"],
        Underline: ["underline"],
        Strikethrough: ["strikethrough"],
        "Blocks (Headings)": ["h1", "h2", "h3", "h4", "h5", "h6", "p", "pre"],
    };

    // Allowed values for Free/Starter plans (exemptions)
    private readonly allowedFontSizes = ["10pt", "11pt", "12pt"];
    private readonly allowedFontNames = [
        "arial,helvetica,sans-serif",
        "calibri",
        "helvetica,sans-serif",
        "arial",
        "helvetica"
    ];

    constructor(private dialogService: DialogService) { }

    // Helper to check if a feature is allowed
    public isAllowed(key: keyof PlanFeatures, features: PlanFeatures, planId: string): boolean {
        // Premium plans (Growth, Sales Pro, Pro Teams) should have all features
        // If features object is missing or incomplete, use planId as fallback
        if (planId === 'Growth' || planId === 'Sales Pro' || planId.startsWith('Pro Teams')) {
            return true; // All features allowed for premium plans
        }

        const val = features?.[key];

        // If features object is empty or feature is undefined, be conservative
        if (val === undefined || val === null) {
            // For Free/Starter plans without proper features, default to restricted
            return false;
        }

        if (typeof val === 'boolean') return val;
        if (typeof val === 'number') return val === -1 || val > 0; // Assuming -1 is unlimited
        return !!val;
    }

    /**
     * Checks if font size is restricted for the current plan.
     * Font Size feature is a number representing how many sizes are allowed.
     * If it's <= the number of allowed sizes (3), the plan is restricted.
     * If it's -1 or a large number (20+), the plan has full access.
     */
    private isFontSizeRestricted(features: PlanFeatures, planId: string): boolean {
        // Premium plans have no restrictions
        if (planId === 'Growth' || planId === 'Sales Pro' || planId.startsWith('Pro Teams')) {
            return false;
        }

        const fontSizeLimit = features?.['Font Size'];

        // If undefined or 0, assume restricted (conservative)
        if (fontSizeLimit === undefined || fontSizeLimit === null || fontSizeLimit === 0) {
            return true;
        }

        // -1 means unlimited
        if (fontSizeLimit === -1) {
            return false;
        }

        // If the limit is <= the number of allowed sizes, it's restricted
        // allowedFontSizes = ["10pt", "11pt", "12pt"] = 3 sizes
        return fontSizeLimit <= this.allowedFontSizes.length;
    }

    /**
     * Checks if font name/family is restricted for the current plan.
     * Fonts feature is a number representing how many fonts are allowed.
     * If it's <= the number of allowed fonts (5), the plan is restricted.
     * If it's -1 or a large number (10+), the plan has full access.
     */
    private isFontNameRestricted(features: PlanFeatures, planId: string): boolean {
        // Premium plans have no restrictions
        if (planId === 'Growth' || planId === 'Sales Pro' || planId.startsWith('Pro Teams')) {
            return false;
        }

        const fontsLimit = features?.['Fonts'];

        // If undefined or 0, assume restricted (conservative)
        if (fontsLimit === undefined || fontsLimit === null || fontsLimit === 0) {
            return true;
        }

        // -1 means unlimited
        if (fontsLimit === -1) {
            return false;
        }

        // If the limit is <= the number of allowed fonts, it's restricted
        // allowedFontNames has 5 fonts
        return fontsLimit <= this.allowedFontNames.length;
    }

    /**
     * Checks if a TinyMCE command should be blocked based on plan restrictions.
     * @returns Object with featureKey and errorKey if blocked, null if allowed
     */
    private checkCommandRestriction(
        e: any,
        features: PlanFeatures,
        planId: string
    ): { featureKey: keyof PlanFeatures; errorKey: string } | null {
        const command = e.command;
        const value = e.value;
        const ui = e.ui;

        // 1. Check direct commands
        for (const [featureKey, commands] of Object.entries(this.directCommandMap)) {
            if (commands.includes(command)) {
                if (!this.isAllowed(featureKey as keyof PlanFeatures, features, planId)) {
                    return {
                        featureKey: featureKey as keyof PlanFeatures,
                        errorKey: this.getErrorKey(featureKey)
                    };
                }
            }
        }

        // 2. Check mceToggleFormat commands (italic, bold, headings, etc.)
        if (command === 'mceToggleFormat' && value) {
            const valueStr = String(value).toLowerCase();

            for (const [featureKey, formatValues] of Object.entries(this.toggleFormatMap)) {
                if (formatValues.includes(valueStr)) {
                    if (!this.isAllowed(featureKey as keyof PlanFeatures, features, planId)) {
                        return {
                            featureKey: featureKey as keyof PlanFeatures,
                            errorKey: this.getErrorKey(featureKey)
                        };
                    }
                }
            }
        }

        // 3. Check color commands (mceApplyTextcolor)
        if (command === 'mceApplyTextcolor') {
            const featureKey = ui === 'hilitecolor' ? 'Background Color' : 'Font Color';
            if (!this.isAllowed(featureKey as keyof PlanFeatures, features, planId)) {
                return {
                    featureKey: featureKey as keyof PlanFeatures,
                    errorKey: ui === 'hilitecolor' ? 'backgroundColor' : 'fontColor'
                };
            }
        }

        // 4. Check FontSize with exemptions (allow 10pt, 11pt, 12pt for restricted plans)
        if (command === 'FontSize') {
            if (this.isFontSizeRestricted(features, planId)) {
                if (!this.allowedFontSizes.includes(value)) {
                    return {
                        featureKey: 'Font Size' as keyof PlanFeatures,
                        errorKey: 'FontSize'
                    };
                }
            }
        }

        // 5. Check FontName with exemptions (allow arial, calibri, helvetica for restricted plans)
        if (command === 'FontName') {
            if (this.isFontNameRestricted(features, planId)) {
                const normalizedValue = String(value).toLowerCase();
                const isAllowedFont = this.allowedFontNames.some(
                    allowed => normalizedValue.includes(allowed.toLowerCase())
                );
                if (!isAllowedFont) {
                    return {
                        featureKey: 'Fonts' as keyof PlanFeatures,
                        errorKey: 'FontName'
                    };
                }
            }
        }

        return null; // Command is allowed
    }

    /**
     * Maps feature key to the error key expected by WarningDialogComponent
     */
    private getErrorKey(featureKey: string): string {
        const errorMapping: Record<string, string> = {
            'Italic': 'italic',
            'Underline': 'underline',
            'Strikethrough': 'strikethrough',
            'Bold': 'generic',
            'Bullet Points': 'InsertUnorderedList',
            'Numbered List': 'InsertOrderedList',
            'Alignment - Left': 'JustifyLeft',
            'Alignment - Centered': 'JustifyCenter',
            'Alignment - Right': 'JustifyRight',
            'Blocks (Headings)': 'blockStyle',
            'Increase Indent': 'indent',
            'Decrease Indent': 'outdent',
            'Font Color': 'fontColor',
            'Background Color': 'backgroundColor',
            'Font Size': 'FontSize',
            'Fonts': 'FontName',
            'Clear Formatting': 'RemoveFormat',
            'Preview': 'mcePreview',
            'Hyperlink': 'mceLink',
            'Images': 'mceUpdateImage',
            'Source Code': 'generic',
        };
        return errorMapping[featureKey] || 'generic';
    }

    /**
     * Determines if we should show the Starter plan upgrade option
     * Features available in Starter but not Free should show Starter upgrade
     */
    private shouldShowStarter(featureKey: keyof PlanFeatures, planId: string): boolean {
        // Features available in Starter but not Free - show Starter upgrade option
        const starterFeatures: string[] = [
            'Italic', 'Underline', 'Strikethrough',
            'Bullet Points', 'Numbered List',
            'Font Size', 'Fonts',
        ];
        return planId === 'Free' && starterFeatures.includes(featureKey as string);
    }

    getPlanBasedConfig(
        features: PlanFeatures,
        baseConfig: any,
        componentInstance: any,
        planId: string,
        openDialogCallback: (type: string, error: string, showTheStarter: boolean) => void
    ): any {
        const config = { ...baseConfig };

        // Setup function with consolidated BeforeExecCommand handler
        config.setup = (editor: any) => {
            // NOTE: We do NOT call baseConfig.setup(editor) here because it contains
            // legacy hardcoded validation logic that conflicts with our dynamic system.
            // The base config's setup function checks for planId !== "Growth" and blocks
            // headings/paragraphs, which interferes with our feature-based restrictions.

            // Single consolidated BeforeExecCommand handler for ALL restrictions
            editor.on('BeforeExecCommand', (e: any) => {
                // Skip non-blocking commands
                if (e.command === 'mceFocus') {
                    return;
                }

                const blocked = this.checkCommandRestriction(e, features, planId);

                if (blocked) {
                    e.preventDefault();
                    e.stopImmediatePropagation();
                    openDialogCallback('editor', blocked.errorKey, this.shouldShowStarter(blocked.featureKey, planId));
                    return false;
                }
            });

            // Paste Preprocess handler for sanitization
            editor.on('PastePreProcess', (e: any) => {
                this.handlePasteSanitization(e, features, openDialogCallback, planId, editor);
            });

            // Override mceRemoveTextcolor to also handle block-level color styles.
            // TinyMCE's default removal only targets inline <span> formats, but pasted
            // content often has colors on block elements (<div>, <p>, etc.).
            editor.on('init', () => {
                editor.addCommand('mceRemoveTextcolor', (ui: string) => {
                    editor.undoManager.transact(() => {
                        editor.focus();
                        // Standard formatter remove (handles inline <span> elements)
                        editor.formatter.remove(ui, { value: null }, null, true);

                        // Also remove color styles from block-level elements
                        const selectedBlocks = editor.selection.getSelectedBlocks();
                        if (selectedBlocks && selectedBlocks.length) {
                            selectedBlocks.forEach((block: HTMLElement) => {
                                if (ui === 'hilitecolor') {
                                    block.style.removeProperty('background-color');
                                    block.style.removeProperty('background');
                                } else {
                                    block.style.removeProperty('color');
                                }
                                // Clean up empty style attribute
                                if (!block.getAttribute('style')?.trim()) {
                                    block.removeAttribute('style');
                                }
                            });
                        }
                        editor.nodeChanged();
                    });
                });
            });
        };

        return config;
    }

    private handlePasteSanitization(
        args: any,
        features: PlanFeatures,
        openDialogCallback: (type: string, error: string, showTheStarter: boolean) => void,
        planId: string,
        editor: any
    ) {
        const content = args.content;
        let hasRestrictedContent = false;
        const restrictedFeatures: string[] = [];

        // Create a temporary DOM element to analyze structure
        const div = document.createElement('div');
        div.innerHTML = content;

        // Check 1: Restricted Tags
        // Map features to tags that SHOULD NOT appear
        const restrictChecks: Array<{ featureName: string, allowed: boolean, tags?: string[], styles?: string[] }> = [
            { featureName: 'Bold', allowed: features['Bold'], tags: ['b', 'strong'], styles: ['font-weight'] },
            { featureName: 'Italic', allowed: features['Italic'], tags: ['i', 'em'], styles: ['font-style'] },
            { featureName: 'Underline', allowed: features['Underline'], tags: ['u'], styles: ['text-decoration'] },
            { featureName: 'Strikethrough', allowed: features['Strikethrough'], tags: ['s', 'strike'], styles: ['text-decoration'] },
            { featureName: 'Hyperlink', allowed: features['Hyperlink'], tags: ['a'] },
            { featureName: 'Numbered List', allowed: features['Numbered List'], tags: ['ol'] },
            { featureName: 'Bullet Points', allowed: features['Bullet Points'], tags: ['ul'] },
            { featureName: 'Images', allowed: features['Images'], tags: ['img'] },
            { featureName: 'Blocks (Headings)', allowed: features['Blocks (Headings)'], tags: ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p'] }
        ];

        for (const check of restrictChecks) {
            if (!check.allowed) {
                // Check tags
                if (check.tags) {
                    for (const tag of check.tags) {
                        if (div.querySelector(tag)) {
                            hasRestrictedContent = true;
                            restrictedFeatures.push(check.featureName);
                            break; // Only add feature once
                        }
                    }
                }
                // Check styles (basic implementation)
                if (!hasRestrictedContent && check.styles) {
                    const styled = div.querySelectorAll('[style]');
                    for (const el of Array.from(styled)) {
                        const htmlEl = el as HTMLElement;
                        for (const styleProp of check.styles) {
                            if (htmlEl.style[styleProp]) {
                                hasRestrictedContent = true;
                                restrictedFeatures.push(check.featureName);
                                break;
                            }
                        }
                        if (hasRestrictedContent) break;
                    }
                }
            }
        }

        // Specific check for Alignment
        const alignChecks = [
            { feat: features["Alignment - Left"], val: 'left', name: 'Left Alignment' },
            { feat: features["Alignment - Centered"], val: 'center', name: 'Center Alignment' },
            { feat: features["Alignment - Right"], val: 'right', name: 'Right Alignment' },
        ];

        alignChecks.forEach(chk => {
            if (!chk.feat) {
                const styled = div.querySelectorAll('[style]');
                for (const el of Array.from(styled)) {
                    const htmlEl = el as HTMLElement;
                    if (htmlEl.style.textAlign === chk.val) {
                        hasRestrictedContent = true;
                        restrictedFeatures.push(chk.name);
                        break;
                    }
                }
                // Also check deprecated align attribute
                const aligned = div.querySelectorAll(`[align="${chk.val}"]`);
                if (aligned.length > 0) {
                    hasRestrictedContent = true;
                    restrictedFeatures.push(chk.name);
                }
            }
        });

        // Check for font colors and background colors
        if (!features['Font Color'] || !features['Background Color']) {
            const styled = div.querySelectorAll('[style]');
            for (const el of Array.from(styled)) {
                const htmlEl = el as HTMLElement;
                if (!features['Font Color'] && htmlEl.style.color) {
                    hasRestrictedContent = true;
                    restrictedFeatures.push('Font Color');
                }
                if (!features['Background Color'] && htmlEl.style.backgroundColor) {
                    hasRestrictedContent = true;
                    restrictedFeatures.push('Background Color');
                }
            }
        }


        if (hasRestrictedContent) {
            const uniqueFeatures = [...new Set(restrictedFeatures)];
            const featureList = uniqueFeatures.join(', ');

            // Block the paste - we'll handle insertion after dialog closes
            const originalContent = args.content;
            args.content = '';

            // Create sanitized version of the content
            const sanitizedContent = this.stripRestrictedFormatting(originalContent, features, planId);

            // Show the dialog with restricted features
            const dialogRef = this.dialogService.openDialogComponent(
                WarningDialogComponent,
                {
                    type: 'pasteFormatting',
                    showTheStarter: planId === 'Free',
                    error: `pasteFormatting`,
                    isCustomMessage: false,
                    restrictedFeatures: featureList
                },
                "800px"
            );

            // When dialog is closed (dismissed, not upgraded), paste the sanitized content
            dialogRef.afterClosed().pipe(take(1)).subscribe((result) => {
                // If result is undefined or 'dismissed', user dismissed the dialog
                // Insert the sanitized content
                if (result !== 'upgraded') {
                    editor.insertContent(sanitizedContent);
                }
            });
        }
    }

    /**
     * Strips restricted formatting from HTML content based on plan features.
     * Returns plain text or partially formatted content depending on allowed features.
     */
    private stripRestrictedFormatting(html: string, features: PlanFeatures, planId: string): string {
        // Premium plans have all features, return content as-is
        if (planId === 'Growth' || planId === 'Sales Pro' || planId.startsWith('Pro Teams')) {
            return html;
        }

        const div = document.createElement('div');
        div.innerHTML = html;

        // Define what tags/styles to strip based on restricted features
        const tagStrips: Array<{ featureName: keyof PlanFeatures, tags: string[] }> = [
            { featureName: 'Bold', tags: ['b', 'strong'] },
            { featureName: 'Italic', tags: ['i', 'em'] },
            { featureName: 'Underline', tags: ['u'] },
            { featureName: 'Strikethrough', tags: ['s', 'strike'] },
            { featureName: 'Hyperlink', tags: ['a'] },
            { featureName: 'Numbered List', tags: ['ol'] },
            { featureName: 'Bullet Points', tags: ['ul'] },
            { featureName: 'Images', tags: ['img'] },
            { featureName: 'Blocks (Headings)', tags: ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p'] }
        ];

        // Strip restricted tags
        for (const strip of tagStrips) {
            if (!this.isAllowed(strip.featureName, features, planId)) {
                for (const tag of strip.tags) {
                    const elements = div.querySelectorAll(tag);
                    elements.forEach((el) => {
                        if (tag === 'img') {
                            // Remove images entirely
                            el.remove();
                        } else if (tag === 'a') {
                            // Replace links with their text content
                            const text = document.createTextNode(el.textContent || '');
                            el.parentNode?.replaceChild(text, el);
                        } else if (tag === 'ol' || tag === 'ul') {
                            // Convert lists to plain text with line breaks
                            const items = el.querySelectorAll('li');
                            const textContent = Array.from(items).map(li => li.textContent).join('<br>');
                            const span = document.createElement('span');
                            span.innerHTML = textContent;
                            el.parentNode?.replaceChild(span, el);
                        } else {
                            // For formatting tags, unwrap them (keep content)
                            const parent = el.parentNode;
                            while (el.firstChild) {
                                parent?.insertBefore(el.firstChild, el);
                            }
                            parent?.removeChild(el);
                        }
                    });
                }
            }
        }

        // Strip restricted styles
        const styleStrips: Array<{ featureName: keyof PlanFeatures, styles: string[], alignVal?: string }> = [
            { featureName: 'Font Color', styles: ['color'] },
            { featureName: 'Background Color', styles: ['backgroundColor', 'background-color'] },
            { featureName: 'Font Size', styles: ['fontSize', 'font-size'] },
            { featureName: 'Fonts', styles: ['fontFamily', 'font-family'] },
            { featureName: 'Alignment - Left', styles: ['textAlign', 'text-align'], alignVal: 'left' },
            { featureName: 'Alignment - Centered', styles: ['textAlign', 'text-align'], alignVal: 'center' },
            { featureName: 'Alignment - Right', styles: ['textAlign', 'text-align'], alignVal: 'right' }
        ];

        for (const strip of styleStrips) {
            if (!this.isAllowed(strip.featureName, features, planId)) {
                const styledElements = div.querySelectorAll('[style]');
                styledElements.forEach((el) => {
                    const htmlEl = el as HTMLElement;
                    for (const style of strip.styles) {
                        // Convert kebab-case to camelCase for accessing style object
                        const camelStyle = style.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
                        if (htmlEl.style[camelStyle as any]) {
                            if (strip.alignVal) {
                                // Only remove if it matches the specific alignment value
                                if (htmlEl.style[camelStyle as any] === strip.alignVal) {
                                    htmlEl.style[camelStyle as any] = '';
                                }
                            } else {
                                htmlEl.style[camelStyle as any] = '';
                            }
                        }
                    }
                });
            }
        }

        // Clean up empty style attributes
        const styledElements = div.querySelectorAll('[style]');
        styledElements.forEach((el) => {
            const htmlEl = el as HTMLElement;
            if (!htmlEl.getAttribute('style')?.trim()) {
                htmlEl.removeAttribute('style');
            }
        });

        // Remove align attributes for restricted alignments
        const alignmentFeatures: Array<{ featureName: keyof PlanFeatures, alignVal: string }> = [
            { featureName: 'Alignment - Left', alignVal: 'left' },
            { featureName: 'Alignment - Centered', alignVal: 'center' },
            { featureName: 'Alignment - Right', alignVal: 'right' }
        ];

        for (const align of alignmentFeatures) {
            if (!this.isAllowed(align.featureName, features, planId)) {
                const aligned = div.querySelectorAll(`[align="${align.alignVal}"]`);
                aligned.forEach((el) => {
                    el.removeAttribute('align');
                });
            }
        }

        return div.innerHTML;
    }

    /**
     * Validates if HTML content is allowed based on plan features and character limits.
     * @returns true if content is valid/allowed, false otherwise.
     */
    public validateShortcutContent(
        html: string,
        plainText: string,
        features: PlanFeatures,
        planId: string,
        rewards: string[] = []
    ): boolean {
        // 1. Plan-based character limit check
        // Check for UNLIMITED_CHARACTERS reward
        const hasUnlimitedChars = rewards.includes('UNLIMITED_CHARACTERS');

        if (!hasUnlimitedChars) {
            const length = plainText.length;
            if (planId === 'Free' && length >= 2500) return false;
            if (planId === 'Starter' && length >= 25000) return false;
        }

        // 2. Feature-based checks
        if (planId === 'Growth' || planId === 'Sales Pro' || planId.startsWith('Pro Teams')) {
            return true;
        }

        // Mapping from tags/styles to features
        const checks = [
            { feat: 'Bold', tags: ['b', 'strong'], styles: ['font-weight'] },
            { feat: 'Italic', tags: ['i', 'em'], styles: ['font-style'] },
            { feat: 'Underline', tags: ['u'], styles: ['text-decoration'] },
            { feat: 'Strikethrough', tags: ['s', 'strike'], styles: ['text-decoration'] },
            { feat: 'Hyperlink', tags: ['a'] },
            { feat: 'Numbered List', tags: ['ol'] },
            { feat: 'Bullet Points', tags: ['ul'] },
            { feat: 'Images', tags: ['img'] },
            { feat: 'Blocks (Headings)', tags: ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'] },
            { feat: 'Alignment - Left', styles: ['text-align'], styleVal: 'left' },
            { feat: 'Alignment - Centered', styles: ['text-align'], styleVal: 'center' },
            { feat: 'Alignment - Right', styles: ['text-align'], styleVal: 'right' },
            { feat: 'Font Color', styles: ['color'] },
            { feat: 'Background Color', styles: ['background-color'] },
            { feat: 'Font Size', styles: ['font-size'] },
            { feat: 'Fonts', styles: ['font-family'] }
        ];

        const div = document.createElement('div');
        div.innerHTML = html;

        for (const check of checks) {
            const featureKey = check.feat as keyof PlanFeatures;
            if (!this.isAllowed(featureKey, features, planId)) {
                // Tag check
                if (check.tags) {
                    for (const tag of check.tags) {
                        if (div.querySelector(tag)) return false;
                    }
                }
                // Style check
                if (check.styles) {
                    const styled = div.querySelectorAll('[style]');
                    for (const el of Array.from(styled)) {
                        const htmlEl = el as HTMLElement;
                        for (const prop of check.styles) {
                            if (htmlEl.style[prop]) {
                                if (check.styleVal) {
                                    if (htmlEl.style[prop] === check.styleVal) return false;
                                } else {
                                    return false;
                                }
                            }
                        }
                    }
                }
            }
        }

        return true;
    }
}
